import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Seo, { Breadcrumbs } from "@/components/Seo";
import { Clock, User, ArrowRight, Calendar } from "lucide-react";

export default function GuideDetail() {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/guides/${slug}`)
      .then((r) => setG(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-bl py-20 text-center" data-testid="guide-not-found">
        Guide not found. <Link to="/guides" className="link-blue">Browse all guides</Link>
      </div>
    );
  }
  if (!g) return <div className="container-bl py-20">Loading…</div>;

  const canonical = typeof window !== "undefined" ? `${window.location.origin}/guides/${g.slug}` : undefined;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": g.title,
    "image": [g.hero_image],
    "datePublished": g.published_at,
    "dateModified": g.published_at,
    "author": { "@type": "Person", "name": g.author },
    "publisher": {
      "@type": "Organization",
      "name": "B&L Rentals",
      "logo": { "@type": "ImageObject", "url": canonical ? `${canonical.split("/guides")[0]}/favicon.ico` : "" },
    },
    "description": g.meta_description || g.excerpt,
    "mainEntityOfPage": canonical,
  };

  return (
    <div className="container-bl py-10 max-w-4xl" data-testid={`page-guide-${g.slug}`}>
      <Seo
        title={`${g.title} | B&L Rentals`}
        description={g.meta_description || g.excerpt}
        canonical={canonical}
        image={g.hero_image}
        type="article"
        schema={articleSchema}
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Guides", to: "/guides" }, { label: g.title }]} />

      <article>
        {/* HEADER */}
        <header className="mb-8">
          <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-widest text-[#E2AB20] font-semibold">
            {g.tags?.slice(0, 3).map((t) => (
              <span key={t} className="bg-[#E2AB20]/8 px-2 py-1 rounded-full">{t}</span>
            ))}
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight" data-testid="guide-title">
            {g.title}
          </h1>
          <p className="mt-4 text-lg text-[#AEB9C9] leading-relaxed">{g.excerpt}</p>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#AEB9C9] pb-6 border-b">
            <span className="inline-flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-[#E2AB20] text-white grid place-items-center font-semibold text-xs">
                {g.author?.[0] || "B"}
              </div>
              <span>
                <span className="font-medium text-white">{g.author}</span>
                {g.author_role && <span className="text-[#AEB9C9]"> · {g.author_role}</span>}
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> {g.published_at}</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {g.read_minutes} min read</span>
          </div>
        </header>

        {/* HERO IMAGE */}
        <div className="rounded-2xl overflow-hidden bg-gray-100 aspect-[16/9]">
          <img src={g.hero_image} alt={g.hero_alt || g.title} title={g.hero_alt || g.title} className="w-full h-full object-cover" />
        </div>

        {/* BODY */}
        <div className="mt-10 space-y-10">
          {g.sections?.map((s, i) => (
            <section key={i} data-testid={`guide-section-${i}`}>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-white leading-snug">
                {s.heading}
              </h2>
              <div className="mt-4 text-[#E5EAF2] leading-relaxed text-base space-y-4 whitespace-pre-line">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl bg-[#E2AB20] text-white p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="guide-cta">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-white/80">Ready to book?</div>
            <h3 className="mt-1 font-heading text-2xl md:text-3xl font-bold">
              See our fleet and book your van today.
            </h3>
            <p className="mt-2 text-white/85 max-w-xl text-sm">
              Auto-applied 3-day, weekly, and 2-week discounts. Guest checkout available.
            </p>
          </div>
          <Link
            to="/vehicles"
            data-testid="guide-cta-btn"
            className="inline-flex items-center gap-2 bg-white text-[#E2AB20] font-semibold px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            View vehicles <ArrowRight size={18} />
          </Link>
        </div>

        {/* AUTHOR CARD */}
        <div className="mt-12 card-bl p-6 flex gap-4 items-start" data-testid="author-card">
          <div className="h-12 w-12 rounded-full bg-[#E2AB20] text-white grid place-items-center font-semibold shrink-0">
            <User size={20} />
          </div>
          <div>
            <div className="text-sm text-[#AEB9C9]">Written by</div>
            <div className="mt-1 font-heading font-semibold text-white">{g.author}</div>
            <div className="text-sm text-[#AEB9C9] mt-0.5">{g.author_role}</div>
            <p className="mt-3 text-sm text-[#AEB9C9] leading-relaxed">
              5-star Turo host serving Oxnard and Ventura County since 2021, with 200+ trips hosted. Reach out anytime with rental questions — <Link to="/contact" className="link-blue">contact page</Link>.
            </p>
          </div>
        </div>

        {/* RELATED LINKS */}
        <nav className="mt-10 flex flex-wrap gap-3" data-testid="guide-related">
          <Link to="/guides" className="btn-secondary text-sm">← All guides</Link>
          <Link to="/vehicles" className="btn-secondary text-sm">Fleet</Link>
          <Link to="/faq" className="btn-secondary text-sm">FAQ</Link>
          <Link to="/insurance-requirements" className="btn-secondary text-sm">Insurance requirements</Link>
        </nav>
      </article>
    </div>
  );
}
